import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import {
	getActivityFile,
	getActivityTimeline,
	getAllLocations,
	getAllSalesReps,
	getCommissionableProdsByQCodes,
	getProductsByClinicsAndPort,
	getSharePointFileContent,
	getSharePointList,
	uploadToActivity,
} from '@/services/common';
import type { AccountProductInfo } from '@/types/account';
import type { CommissionableProduct } from '@/types/orderReturns';
import type { UserActivityTimeline } from '@/types/users';

export const useGetLocations = ({
	page = 1,
	limit = 50,
	search = '',
	filterIds = '',
	enabled = true,
}: {
	page?: number;
	limit?: number;
	search?: string;
	filterIds?: string;
	enabled?: boolean;
}) => {
	return useQuery({
		queryKey: ['locations', page, limit, search, filterIds],
		queryFn: () => getAllLocations(page, limit, search, filterIds),
		enabled,
		keepPreviousData: true,
		staleTime: 5 * 60 * 1000, // 5 minutes
		cacheTime: 10 * 60 * 1000, // 10 minutes
	});
};

export const useGetProductClinicPort = (
	{
		clinicIds = [],
		port,
		enabled = true,
	}: {
		clinicIds: string[];
		port: string;
		enabled?: boolean;
	},
	onSuccess?: (data: AccountProductInfo[]) => void,
) => {
	return useQuery({
		queryKey: ['product-clinics-port', clinicIds, port],
		queryFn: () => getProductsByClinicsAndPort(clinicIds, port),
		enabled,
		keepPreviousData: false,
		// cacheTime: 0,
		onSuccess,
	});
};

export const useGetSalesReps = ({
	page = 1,
	limit = 50,
	search = '',
	filterIds = '',
	enabled = true,
}: {
	page?: number;
	limit?: number;
	search?: string;
	filterIds?: string;
	enabled?: boolean;
}) => {
	return useQuery({
		queryKey: ['salesReps', page, limit, search, filterIds],
		queryFn: () => getAllSalesReps(page, limit, search, filterIds),
		enabled,
		keepPreviousData: false,
		// staleTime: 5 * 60 * 1000, // 5 minutes
		// cacheTime: 10 * 60 * 1000, // 10 minutes
	});
};

export const useGetSharePointList = ({
	clinic,
	folder,
	sort = '',
	columnsFilters = '[]',
	fileType = '',
	enabled = true,
}: {
	clinic: string;
	folder?: string;
	sort?: string;
	columnsFilters?: string;
	fileType?: string;
	enabled?: boolean;
}) => {
	return useQuery({
		queryKey: [
			'sharePointList',
			clinic,
			folder,
			sort,
			columnsFilters,
			fileType,
		],
		queryFn: () =>
			getSharePointList(clinic, folder, sort, columnsFilters, fileType),
		enabled: enabled && !!clinic,
		keepPreviousData: false,
		// staleTime: 2 * 60 * 1000, // 2 minutes
		// cacheTime: 5 * 60 * 1000, // 5 minutes
	});
};

export const useGetSharePointFileContent = ({
	clinic,
	folderName,
	fileName,
	enabled = true,
}: {
	clinic: string;
	folderName: string;
	fileName: string;
	enabled?: boolean;
}) => {
	return useQuery({
		queryKey: ['sharePointFile', clinic, folderName, fileName],
		queryFn: () => getSharePointFileContent(clinic, folderName, fileName),
		enabled: enabled && !!clinic && !!fileName,
		// cacheTime: 0,
	});
};

export const useGetCommissionableProdsByQCodes = (
  {
    qcodes = '',
    enabled = true,
  }: {
    qcodes: string;
    enabled?: boolean;
  },
  onSuccess?: (data: CommissionableProduct[]) => void,
) => {
  return useQuery({
    queryKey: ['products', qcodes],
    queryFn: () => getCommissionableProdsByQCodes({ qcodes }),
    enabled,
    keepPreviousData: false,
    // cacheTime: 0,
    onSuccess,
  });
};

export const useGetUserActivityTimeline = (
	{
		userId,
	}: {
		userId: string;
	},
	onSuccess?: (data: {
		annotations: UserActivityTimeline[];
	}) => void,
) => {
	return useQuery({
		queryKey: ['user-activity-timelines', userId],
		queryFn: () => getActivityTimeline(userId),
		enabled: !!userId,
		keepPreviousData: false,
		// cacheTime: 0,
		onSuccess,
	});
};

export const useGetActivityFile = (
  onSuccess?: (data: any) => void,
) => {
  return useMutation({
    mutationFn: ({ url }: { url: string }) => getActivityFile(url),
    onSuccess,
  });
};

export function useGetUserActivityTimelineInfinite(filters: {
	userId: string;
	limit?: number;
}) {
	const limit = filters.limit ?? 50;

	return useInfiniteQuery(
		["user-activity-timeline-infinite", filters],
		async ({ pageParam = 1 }) => {
			// ✅ IMPORTANT: do NOT use filters.page here; infinite query controls the page
			return getActivityTimeline(
				filters.userId,
				pageParam, limit
			);
		},
		{
			getNextPageParam: (lastPage, allPages) => {
				// ✅ best: totalRecords present
				const total = lastPage?.totalRecords;
				if (typeof total === "number") {
					const loaded = allPages.reduce(
						(sum, p) => sum + (p?.annotations?.length ?? 0),
						0
					);
					return loaded < total ? allPages.length + 1 : undefined;
				}

				const lastCount = lastPage?.annotations?.length ?? 0;
				return lastCount === limit ? allPages.length + 1 : undefined;
			},
			staleTime: 30_000,
			keepPreviousData: true,
		}
	);
}

export const useUploadToActivity = (
  onSuccess?: (data: any) => void,
) => {
  return useMutation({
    mutationFn: ({ activityId, text, files }: { activityId: string; text: string; files?: Array<{ filename: string; documentbody: string; mimetype: string }> }) => uploadToActivity(activityId, text, files),
    onSuccess,
  });
};
